import React, { useEffect, useRef, useState } from 'react';
import api from '../../utils/axios';
import { toast } from 'sonner';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { StatCard } from './components/StatCard';
import { SectionStatusChip } from './components/SectionStatusChip';
import { KpiTimeChart } from './components/KpiTimeChart';
import { HelpTip } from './components/HelpTip';
import { SectionExportButton } from './components/SectionExportButton';
import { GlosarioHelpButton } from './components/GlosarioHelpButton';
import { fmtNum, fmtNumShort, fmtPeriodo, CHART_COLORS } from './components/kpiUtils';
import { LifeBuoy, Upload, Inbox, CheckCircle2, Clock } from 'lucide-react';

const fmtHoras = (h) => {
  if (h === null || h === undefined) return '—';
  if (h < 1) return `${Math.round(h * 60)} min`;
  if (h < 48) return `${h.toFixed(1)} h`;
  return `${(h / 24).toFixed(1)} d`;
};

const KpisAtencion = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const fileRef = useRef(null);
  const rootRef = useRef(null);

  const load = async () => {
    try { const r = await api.get('/kpis/atencion'); setData(r.data); }
    catch (e) { toast.error('Error al cargar Atención al Cliente'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const subir = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const r = await api.post('/kpis/atencion/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(`${r.data.total} tickets importados`); load();
    } catch (err) { toast.error(err.response?.data?.detail || 'No se pudo importar'); }
    finally { setImporting(false); if (fileRef.current) fileRef.current.value = ''; }
  };

  const k = data?.kpis || {};
  const puede_cargar = data?.puede_cargar;
  const sla = data?.sla || {};

  if (loading) return <div className="p-8 text-slate-500" data-testid="kpis-atencion-loading">Cargando tickets…</div>;

  return (
    <div className="p-6 lg:p-8 max-w-7xl mx-auto" ref={rootRef} data-testid="kpis-atencion">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2.5 rounded-xl bg-sky-500/10"><LifeBuoy className="h-6 w-6 text-sky-600" /></div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 dark:text-white">Atención al Cliente</h1>
            <div className="mt-1.5"><SectionStatusChip seccionKey="kpis-atencion" /></div>
            <p className="text-sm text-slate-500">Tickets, estados, abiertos por grupo, canales y motivos de consulta</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <GlosarioHelpButton seccion="atencion" testId="atencion-help-btn" />
          <SectionExportButton title="Atención al Cliente" targetRef={rootRef} testId="atencion-export-btn" />
          {puede_cargar && (
            <>
              <Button onClick={() => fileRef.current?.click()} disabled={importing} className="bg-sky-600 hover:bg-sky-700 active:scale-95 transition-transform" data-testid="atencion-subir-btn">
                <Upload className="h-4 w-4 mr-1" /> {importing ? 'Importando…' : 'Subir tickets (CSV)'}
              </Button>
              <input ref={fileRef} type="file" accept=".csv" className="hidden" onChange={subir} data-testid="atencion-file-input" />
            </>
          )}
        </div>
      </div>

      {!data?.cargado ? (
        <Card className="p-10 text-center border-dashed border-2 border-slate-200 dark:border-slate-700" data-testid="atencion-empty">
          <LifeBuoy className="h-10 w-10 mx-auto text-slate-300 mb-3" />
          <p className="text-slate-500">Todavía no hay tickets cargados.</p>
          <p className="text-sm text-slate-400 mt-1">{puede_cargar ? 'Subí el CSV de tickets (export de la mesa de ayuda).' : 'Pedile a un administrador que cargue el reporte.'}</p>
        </Card>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
            <StatCard label="Tickets" value={fmtNum(k.total)} icon={Inbox} accent="#4f46e5" testId="atencion-kpi-total" />
            <StatCard label="Abiertos" value={fmtNum(k.abiertos)} icon={Clock} accent="#f59e0b" testId="atencion-kpi-abiertos" />
            <StatCard label="Cerrados" value={fmtNum(k.cerrados)} icon={CheckCircle2} accent="#10b981" testId="atencion-kpi-cerrados" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            <Card className="p-5 lg:p-6" data-testid="atencion-por-mes">
              <h2 className="text-base font-semibold text-slate-800 dark:text-slate-200 mb-4">Tickets por mes</h2>
              <KpiTimeChart data={(data?.por_mes || []).map((m) => ({ label: fmtPeriodo(m.periodo, true), total: m.total }))}
                series={[{ key: 'total', name: 'Tickets', color: '#4f46e5' }]} type="area" valueFormatter={fmtNum} axisFormatter={fmtNumShort} height={280} showLegend={false} testId="atencion-mes-chart" />
            </Card>
            <Card className="p-5 lg:p-6" data-testid="atencion-por-estado">
              <h2 className="text-base font-semibold text-slate-800 dark:text-slate-200 mb-4">Tickets por estado</h2>
              <KpiTimeChart data={(data?.por_estado || []).map((e) => ({ label: e.estado, total: e.total }))} xKey="label"
                series={[{ key: 'total', name: 'Tickets', color: '#0ea5e9' }]} type="barras" barLayout="vertical" cellColors={CHART_COLORS}
                height={Math.max(180, (data?.por_estado || []).length * 40)} valueFormatter={fmtNum} axisFormatter={fmtNumShort} showLegend={false} testId="atencion-estado-chart" />
            </Card>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            <Card className="p-5 lg:p-6" data-testid="atencion-abiertos-por-grupo">
              <div className="flex items-center gap-2 mb-4">
                <h2 className="text-base font-semibold text-slate-800 dark:text-slate-200">Abiertos por grupo</h2>
                <HelpTip testId="atencion-abiertos-grupo-help" text="Tickets sin cerrar, por área que los atiende. El grupo 'No Group' del CSV se muestra como 'Whatsapp'." />
              </div>
              <KpiTimeChart data={(data?.por_grupo_abiertos || []).map((g) => ({ label: g.grupo, total: g.total }))} xKey="label"
                series={[{ key: 'total', name: 'Abiertos', color: '#f59e0b' }]} type="barras" barLayout="vertical" cellColors={CHART_COLORS}
                height={Math.max(160, (data?.por_grupo_abiertos || []).length * 42)} valueFormatter={fmtNum} axisFormatter={fmtNumShort} showLegend={false} testId="atencion-abiertos-grupo-chart" />
            </Card>
            <Card className="p-5 lg:p-6" data-testid="atencion-por-grupo">
              <div className="flex items-center gap-2 mb-4">
                <h2 className="text-base font-semibold text-slate-800 dark:text-slate-200">Por grupo (total)</h2>
                <HelpTip testId="atencion-grupo-help" text="Área que atendió el ticket (ej. Compliance, Customer Support). 'No Group' se muestra como 'Whatsapp'." />
              </div>
              <KpiTimeChart data={(data?.por_grupo || []).map((g) => ({ label: g.grupo, total: g.total }))} xKey="label"
                series={[{ key: 'total', name: 'Tickets', color: '#10b981' }]} type="barras" barLayout="vertical" cellColors={CHART_COLORS}
                height={Math.max(160, (data?.por_grupo || []).length * 42)} valueFormatter={fmtNum} axisFormatter={fmtNumShort} showLegend={false} testId="atencion-grupo-chart" />
            </Card>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            <Card className="p-5 lg:p-6" data-testid="atencion-por-fuente">
              <div className="flex items-center gap-2 mb-4">
                <h2 className="text-base font-semibold text-slate-800 dark:text-slate-200">Por canal / fuente</h2>
                <HelpTip testId="atencion-fuente-help" text="Origen del ticket. Se consideran los canales Chat y Email." />
              </div>
              <KpiTimeChart data={(data?.por_fuente || []).map((f) => ({ label: f.fuente, total: f.total }))} xKey="label"
                series={[{ key: 'total', name: 'Tickets', color: '#0ea5e9' }]} type="barras" barLayout="vertical" cellColors={CHART_COLORS}
                height={Math.max(160, (data?.por_fuente || []).length * 48)} valueFormatter={fmtNum} axisFormatter={fmtNumShort} showLegend={false} testId="atencion-fuente-chart" />
            </Card>
            <Card className="p-5 lg:p-6" data-testid="atencion-por-motivo">
              <h2 className="text-base font-semibold text-slate-800 dark:text-slate-200 mb-4">Top 10 motivos de consulta</h2>
              <KpiTimeChart data={(data?.por_motivo || []).slice(0, 10).map((m) => ({ label: m.motivo, total: m.total }))} xKey="label"
                series={[{ key: 'total', name: 'Tickets', color: '#6366f1' }]} type="barras" barLayout="vertical" cellColors={CHART_COLORS}
                height={Math.max(180, Math.min(10, (data?.por_motivo || []).length) * 34)} valueFormatter={fmtNum} axisFormatter={fmtNumShort} showLegend={false} testId="atencion-motivo-chart" />
            </Card>
          </div>

          <Card className="p-5 lg:p-6 mb-6" data-testid="atencion-motivo-detalle">
            <div className="flex items-center gap-2 mb-4">
              <h2 className="text-base font-semibold text-slate-800 dark:text-slate-200">Detalle de motivos de consulta</h2>
              <HelpTip testId="atencion-motivo-detalle-help" text="Todos los motivos de consulta con su cantidad y porcentaje sobre el total de tickets del período." />
              <span className="text-xs text-slate-400">({(data?.por_motivo || []).length} motivos)</span>
            </div>
            {(data?.por_motivo || []).length === 0 ? (
              <p className="text-sm text-slate-400" data-testid="atencion-motivo-detalle-empty">Sin motivos registrados.</p>
            ) : (
              <div className="overflow-auto max-h-[420px] rounded-lg border border-slate-100 dark:border-slate-800">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-slate-50 dark:bg-slate-800/70 backdrop-blur">
                    <tr className="text-left text-slate-500">
                      <th className="py-2 px-3 font-medium w-10">#</th>
                      <th className="py-2 px-3 font-medium">Motivo</th>
                      <th className="py-2 px-3 font-medium text-right w-24">Cantidad</th>
                      <th className="py-2 px-3 font-medium w-52">% del total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(data?.por_motivo || []).map((m, i) => {
                      const pct = k.total ? (m.total / k.total) * 100 : 0;
                      return (
                        <tr key={i} className="border-t border-slate-100 dark:border-slate-800 hover:bg-slate-50/70 dark:hover:bg-slate-800/30" data-testid={`atencion-motivo-row-${i}`}>
                          <td className="py-2 px-3 text-slate-400 tabular-nums">{i + 1}</td>
                          <td className="py-2 px-3 text-slate-700 dark:text-slate-200">{m.motivo}</td>
                          <td className="py-2 px-3 text-right font-semibold text-slate-800 dark:text-slate-100 tabular-nums" data-testid={`atencion-motivo-total-${i}`}>{fmtNum(m.total)}</td>
                          <td className="py-2 px-3">
                            <div className="flex items-center gap-2">
                              <div className="flex-1 h-2 rounded-full bg-slate-100 dark:bg-slate-700 overflow-hidden">
                                <div className="h-full rounded-full bg-indigo-500" style={{ width: `${Math.max(2, pct)}%` }} />
                              </div>
                              <span className="text-xs text-slate-500 tabular-nums w-12 text-right">{pct.toFixed(1)}%</span>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
          <p className="text-xs text-slate-400 mt-4">Fuente: {data?.archivo || 'Export de tickets'} · cargado {data?.fecha_carga ? new Date(data.fecha_carga).toLocaleString('es-AR') : '—'}</p>
        </>
      )}
    </div>
  );
};

export default KpisAtencion;
